// espelho de shared/src/tutoriais/index.ts: manter igual
/**
 * Conteúdo dos tutoriais, como dados puros (sem React nem rede): Web e Mobo só desenham.
 *
 * Contrato das âncoras: `alvo` é um id estável. Cada app marca o elemento da tela com esse id
 * (Web: atributo data-tutorial="<alvo>"; Mobo: registro de refs). Se o app não tiver a âncora
 * naquele momento (tela mudou, plano bloqueou, elemento não existe), o guia PULA o passo, sem travar.
 *
 * `recurso`: chave de `GET api/tutoriais/planos` (catálogo `veiculos.*` ou recursos antigos como
 * `notas_fiscais`, `lista_compras`, `recorrencias`). Sem `recurso` o tutorial é livre. Um tutorial de
 * recurso bloqueado aparece na aba Tutoriais com "Disponível no plano X".
 *
 * Âncoras novas (fluxo "Conta" com bifurcação; a Pluggy só aparece nelas):
 *  - conta.manual   botão "Nova conta" da tela de contas (caminho manual; depois abre o saldo inicial e corte)
 *  - conta.conectar botão "Conectar banco" (opcional), na tela de contas e na tela da Pluggy
 *  - conta.aviso    tela curta de aviso mostrada ANTES do widget de conexão (ou do cadastro das chaves)
 *  - corte.data / corte.saldo / corte.confirmar   existem no assistente da Pluggy e no modal manual de saldo inicial
 *  - cartao.novo, csv.cartao, csv.importar, csv.arquivo, csv.mapear, csv.revisar   cartão e importação da fatura (CSV)
 *  - pluggy.credenciais (formulário de chaves em Configurações), pluggy.vincular (botão Vincular da conta conectada)
 *
 * Cada feature nova adiciona o seu tutorial aqui (um objeto em `TUTORIAIS`).
 */

export type AvancoPasso = 'proximo' | 'clique'

export interface PassoTutorial {
  id: string
  /** Id da âncora na tela (ver convenção acima). */
  alvo: string
  titulo: string
  texto: string
  /** Rota para abrir antes do passo, por plataforma (quando o alvo mora em outra tela). */
  rotas?: { web?: string; mobo?: string }
  /** 'proximo' (padrão): o usuário toca em Próximo. 'clique': também avança ao tocar no alvo. */
  avanco?: AvancoPasso
  /** Passo do cartão "Primeiros passos" a que este passo pertence (para abrir o guia no ponto certo). */
  passoOnboarding?: 'conta' | 'corte_saldo' | 'cartao' | 'categorias' | 'orcamento'
}

export interface Tutorial {
  id: string
  titulo: string
  resumo: string
  /** Nome neutro do ícone; cada app mapeia para o seu conjunto. */
  icone: 'estrela' | 'nota' | 'lista' | 'carro' | 'lancamento' | 'recorrencia' | 'banco'
  recurso?: string
  passos: PassoTutorial[]
}

export const TUTORIAIS: Tutorial[] = [
  {
    id: 'primeiros-passos',
    titulo: 'Primeiros passos',
    resumo: 'Conta, saldo, cartão, categorias e orçamento',
    icone: 'estrela',
    passos: [
      {
        id: 'conta-manual', alvo: 'conta.manual', passoOnboarding: 'conta', avanco: 'clique',
        rotas: { web: '/contas', mobo: '/contas' },
        titulo: 'Crie sua conta',
        texto: 'Dois caminhos, do mesmo jeito. Criar conta manual: você cadastra a conta e informa o saldo de hoje, sem depender de nada externo. Conectar banco (opcional): veja o botão ao lado. O app funciona completo nos dois.',
      },
      {
        id: 'conta-conectar', alvo: 'conta.conectar', passoOnboarding: 'conta', avanco: 'clique',
        titulo: 'Conectar banco (opcional)',
        texto: 'Se preferir, conecte o banco para trazer o saldo e os lançamentos. Antes de abrir a conexão mostramos um aviso curto. Se não quiser, siga com a conta manual.',
      },
      {
        id: 'conta-aviso', alvo: 'conta.aviso', passoOnboarding: 'conta',
        titulo: 'Antes de conectar',
        texto: 'A conexão é feita por uma empresa independente. O Walleon só lê saldo e lançamentos, você pode desconectar quando quiser e pedir a exclusão dos dados.',
      },
      {
        id: 'corte-data', alvo: 'corte.data', passoOnboarding: 'corte_saldo',
        titulo: 'Data de corte',
        texto: 'Escolha a partir de quando você quer que os lançamentos contem no saldo. Sugerimos o 1º do mês atual. Mais para trás traz mais histórico, mas pode mexer em orçamentos de meses anteriores.',
      },
      {
        id: 'corte-saldo', alvo: 'corte.saldo', passoOnboarding: 'corte_saldo',
        titulo: 'Saldo de hoje',
        texto: 'Informe o saldo que a conta tem hoje. Se você conectou um banco, o app já traz o valor; se estiver diferente do que você vê, corrija.',
      },
      {
        id: 'corte-confirmar', alvo: 'corte.confirmar', passoOnboarding: 'corte_saldo', avanco: 'clique',
        titulo: 'Confirmar e importar',
        texto: 'Ao confirmar, o app calcula o saldo inicial na data de corte, sem criar lançamento de ajuste. Lançamentos anteriores ao corte ficam só como histórico.',
      },
      {
        id: 'cartao', alvo: 'cartao.novo', passoOnboarding: 'cartao',
        rotas: { web: '/cartoes', mobo: '/cartoes' },
        titulo: 'Cartão de crédito (opcional)',
        texto: 'Cadastre o seu cartão com limite, fechamento e vencimento. Depois, use Importar fatura (CSV) para trazer as compras do arquivo do banco. Compras de cartão entram na fatura e não mexem no saldo da conta até a fatura ser paga.',
      },
      {
        id: 'categorias', alvo: 'categorias.lista', passoOnboarding: 'categorias',
        rotas: { web: '/categorias', mobo: '/categorias' },
        titulo: 'Categorias (opcional)',
        texto: 'Já vêm categorias prontas. Renomeie, desative as que não usa e crie as suas.',
      },
      {
        id: 'orcamento', alvo: 'orcamento.novo', passoOnboarding: 'orcamento',
        rotas: { web: '/orcamento', mobo: '/orcamento' },
        titulo: 'Primeiro orçamento (opcional)',
        texto: 'Defina quanto quer gastar numa categoria no mês. O app avisa quando chegar perto do limite.',
      },
    ],
  },
  {
    id: 'importar-fatura-csv',
    titulo: 'Importar fatura (CSV ou PDF)',
    resumo: 'Trazer as compras do cartão a partir do arquivo CSV ou do PDF da fatura',
    icone: 'lista',
    passos: [
      {
        id: 'cartao', alvo: 'csv.cartao',
        rotas: { web: '/cartoes', mobo: '/cartoes' },
        titulo: 'Escolha o cartão',
        texto: 'Selecione o cartão da fatura que você quer importar. Se ainda não cadastrou, crie o cartão primeiro.',
      },
      {
        id: 'importar', alvo: 'csv.importar', avanco: 'clique',
        titulo: 'Importar fatura',
        texto: 'Baixe o arquivo da fatura no app ou site do banco: CSV ou PDF. O PDF hoje só é lido para faturas do Mercado Pago; para os outros bancos use o CSV. Toque aqui para começar a importação.',
      },
      {
        id: 'arquivo', alvo: 'csv.arquivo',
        titulo: 'Escolha o arquivo',
        texto: 'Escolha a fatura de destino (ou deixe automática) e selecione o arquivo CSV ou PDF baixado. No PDF, a fatura de destino é a do vencimento que está no próprio arquivo.',
      },
      {
        id: 'mapear', alvo: 'csv.mapear',
        titulo: 'Mapear as colunas',
        texto: 'Só no CSV: se o app não reconhecer as colunas sozinho, indique quais são a data, a descrição e o valor. O exemplo mostra a primeira linha do arquivo. No PDF esta etapa não existe.',
      },
      {
        id: 'revisar', alvo: 'csv.revisar',
        titulo: 'Conferir antes de importar',
        texto: 'Cada linha é comparada com o que já está lançado: o que já existia é só marcado, e entra apenas o que faltar. Desmarque o que não quiser e confirme. No PDF você também pode corrigir data, descrição, valor e parcela, e o app compara o total da fatura com a soma das linhas.',
      },
    ],
  },
  {
    id: 'conectar-banco',
    titulo: 'Conectar banco (opcional)',
    resumo: 'Trazer saldo e lançamentos do banco, com as suas chaves da Pluggy',
    icone: 'banco',
    recurso: 'open_finance',
    passos: [
      {
        id: 'credenciais', alvo: 'pluggy.credenciais',
        rotas: { web: '/configuracoes', mobo: '/configuracoes' },
        titulo: 'Suas chaves da Pluggy',
        texto: 'Cada pessoa usa a própria conta na Pluggy (Meu Pluggy). Crie a conta lá, copie o Client ID e o Client Secret e salve aqui.',
      },
      {
        id: 'conectar', alvo: 'conta.conectar', avanco: 'clique',
        rotas: { web: '/pluggy', mobo: '/pluggy' },
        titulo: 'Conectar banco',
        texto: 'Toque para conectar. Antes de abrir a conexão você vê um aviso curto.',
      },
      {
        id: 'aviso', alvo: 'conta.aviso',
        titulo: 'Leia o aviso',
        texto: 'A conexão é feita pela Pluggy, empresa independente. O Walleon só lê saldo e lançamentos. Você pode desconectar quando quiser e pedir a exclusão dos dados.',
      },
      {
        id: 'vincular', alvo: 'pluggy.vincular',
        titulo: 'Vincule à sua conta',
        texto: 'Vincule a conta do banco a uma conta do Walleon (ou crie uma nova). Depois escolha a data de corte e confirme o saldo.',
      },
    ],
  },
  {
    id: 'notas-fiscais',
    titulo: 'Notas fiscais',
    resumo: 'Escanear a nota e ver os itens',
    icone: 'nota',
    recurso: 'notas_fiscais',
    passos: [
      {
        id: 'escanear', alvo: 'nota.escanear', avanco: 'clique',
        rotas: { web: '/notas-fiscais', mobo: '/notas' },
        titulo: 'Escaneie a nota',
        texto: 'Aponte a câmera para o QR code da nota fiscal (NFC-e) ou digite a chave de 44 dígitos. O app lê os itens e o estabelecimento.',
      },
      {
        id: 'itens', alvo: 'nota.itens',
        titulo: 'Itens da compra',
        texto: 'Cada item ganha uma categoria. Corrija quando errar: o app aprende e usa a correção nas próximas notas.',
      },
      {
        id: 'vinculo', alvo: 'nota.vinculo',
        titulo: 'Vincule ao lançamento',
        texto: 'Ligue a nota ao lançamento da compra, ou crie o lançamento a partir dela, para o gasto aparecer com os itens.',
      },
    ],
  },
  {
    id: 'lista-de-compras',
    titulo: 'Lista de compras',
    resumo: 'Montar, dar baixa no mercado e conciliar com a nota',
    icone: 'lista',
    recurso: 'lista_compras',
    passos: [
      {
        id: 'nova', alvo: 'lista.nova', avanco: 'clique',
        rotas: { web: '/listas', mobo: '/listas' },
        titulo: 'Crie uma lista',
        texto: 'Crie a lista da compra do mês. Dá para compartilhar com quem faz a compra junto.',
      },
      {
        id: 'item', alvo: 'lista.item',
        titulo: 'Adicione itens',
        texto: 'Escolha um grupo (como "Leite em pó") e, se quiser, fixe a marca ou o peso. A dica mostra a última compra.',
      },
      {
        id: 'baixa', alvo: 'lista.baixa',
        titulo: 'Dê baixa no mercado',
        texto: 'Toque no item ao colocar no carrinho. Para quantidades maiores, informe quantos você comprou.',
      },
      {
        id: 'escanear', alvo: 'lista.escanear',
        titulo: 'Escaneie a nota',
        texto: 'Ao escanear a nota da compra, o app propõe a baixa dos itens e o que faltou. Você escolhe manter para a próxima compra ou descartar.',
      },
    ],
  },
  {
    id: 'garagem',
    titulo: 'Garagem',
    resumo: 'Cadastrar o veículo e registrar abastecimentos',
    icone: 'carro',
    recurso: 'veiculos.modulo',
    passos: [
      {
        id: 'novo', alvo: 'garagem.novo', avanco: 'clique',
        rotas: { web: '/veiculos', mobo: '/veiculos' },
        titulo: 'Cadastre o veículo',
        texto: 'Escolha o modelo pela tabela FIPE ou cadastre à mão, e informe o km atual.',
      },
      {
        id: 'abastecer', alvo: 'garagem.abastecer',
        titulo: 'Registre o abastecimento',
        texto: 'Informe km, litros e valor, e marque se encheu o tanque ou se estava na reserva. Com isso o app calcula o consumo.',
      },
      {
        id: 'consumo', alvo: 'garagem.consumo',
        titulo: 'Acompanhe o consumo',
        texto: 'Veja média, mediana, custo por km e o que o último abastecimento rendeu.',
      },
    ],
  },
  {
    id: 'lancamentos',
    titulo: 'Lançamentos',
    resumo: 'Receitas, despesas, transferências e a divisão em cartões',
    icone: 'lancamento',
    passos: [
      {
        id: 'novo', alvo: 'lancamento.novo', avanco: 'clique',
        rotas: { web: '/transacoes', mobo: '/transacoes' },
        titulo: 'Novo lançamento',
        texto: 'Registre uma receita, uma despesa ou uma transferência entre contas.',
      },
      {
        id: 'pagamento', alvo: 'lancamento.pagamento',
        titulo: 'Forma de pagamento',
        texto: 'Escolha a conta ou o cartão. Compra no crédito entra na fatura e pode ser parcelada.',
      },
      {
        id: 'dividir', alvo: 'lancamento.dividir',
        titulo: 'Dividir em cartões',
        texto: 'Pagou parte num cartão e parte em outro? Divida a compra: o último cartão fica com o resto.',
      },
    ],
  },
  {
    id: 'recorrencias',
    titulo: 'Recorrências',
    resumo: 'Contas que se repetem todo mês',
    icone: 'recorrencia',
    recurso: 'recorrencias',
    passos: [
      {
        id: 'nova', alvo: 'recorrencia.nova', avanco: 'clique',
        rotas: { web: '/recorrencias', mobo: '/recorrencias' },
        titulo: 'Nova recorrência',
        texto: 'Cadastre aluguel, assinaturas e contas fixas uma vez, e o app lança todo mês.',
      },
      {
        id: 'periodicidade', alvo: 'recorrencia.periodicidade',
        titulo: 'Periodicidade',
        texto: 'Escolha de quanto em quanto tempo repete e quando termina (ou parcelas).',
      },
    ],
  },
]

export function tutorialPorId(id: string): Tutorial | undefined {
  return TUTORIAIS.find((t) => t.id === id)
}
